/**
 * WhatsNewDialog.jsx — one-time "Welcome / What's New" popup (Jun 2026).
 *
 * Shows once per user per release: a short welcome for first-timers plus a
 * role-aware list of recent features. "Skip" and "Got it" both dismiss and
 * remember (localStorage). Bump WHATS_NEW_VERSION when new features ship and
 * every user sees the fresh list exactly once on their next visit.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePromptSlot } from '../utils/onboardingQueue';
import {
  Sparkles, Clock, MapPin, Briefcase, CreditCard, X, Users, TrendingUp, Repeat, Hash, Mail, BellRing, BookOpen,
} from 'lucide-react';

export const WHATS_NEW_VERSION = '9.1';
const SEEN_KEY = 'ht_whats_new_seen';

export const isWhatsNewPending = () =>
  localStorage.getItem(SEEN_KEY) !== WHATS_NEW_VERSION;

const CORE_FEATURES = [
  { icon: Clock, text: 'Clock in/out from the pill in the top bar or the My Day page — GPS tags your location automatically.' },
  { icon: Briefcase, text: 'Jobs: start, pause, photos, notes and travel time all live inside each job card.' },
  { icon: MapPin, text: 'On-site verification via GPS geofence, Site QR posters, or wall-mounted kiosk tablets.' },
  { icon: CreditCard, text: 'Hours flow to the Payroll Workbench and push to QuickBooks when approved.' },
];

const WHATS_NEW = [
  { icon: Hash, roles: ['admin'], title: 'Invoice numbering — set once, runs in sequence', text: 'Every invoice takes the next number on first download / email / QuickBooks push and keeps it forever. Manual override with audit trail; Invoice Register report.' },
  { icon: Repeat, roles: ['admin'], title: 'Recurring invoices', text: 'Repeat a Daily Site Invoice or a completed job\u2019s invoice on a schedule — drafts you review (Issue all) or issued automatically. See everything under Settings → Recurring & Scheduled.' },
  { icon: Mail, roles: ['admin'], title: 'Customer statements & scheduled reports', text: 'Branded statement PDF for any date range, weekly / monthly / month-end reports per customer, privacy-safe by default, plus a self-serve customer account page.' },
  { icon: BellRing, roles: ['admin'], title: 'Payment reminders', text: 'Automatic past-due emails with the statement attached — 3 / 14 / 30 days after the due date, per customer, with Send now.' },
  { icon: TrendingUp, roles: ['admin', 'supervisor'], title: 'Dashboard tiles on real data', text: 'Estimate performance shows the whole pipeline including drafts; Revenue by Project sums actual invoices; profitability falls back to by-customer.' },
  { icon: Briefcase, roles: ['user'], title: 'Your jobs only', text: 'The Jobs list shows the jobs assigned to you (or your team, if your company allows) — not the whole company\u2019s.' },
  { icon: BookOpen, roles: ['admin', 'supervisor', 'user'], title: 'Manual v9.1 & Admin Setup guides', text: 'Help & Manual → Admin Setup: one configuration guide per feature; the Invoicing guide now covers recurring invoices, statements and reminders in EN / ES / FR.' },
];

export const WhatsNewDialog = () => {
  const { user } = useAuth();
  const [wants, setWants] = useState(false);
  const [firstTime, setFirstTime] = useState(false);
  const open = usePromptSlot('whatsnew', wants);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(SEEN_KEY);
    if (seen === WHATS_NEW_VERSION) return;
    setFirstTime(!seen);
    const t = setTimeout(() => setWants(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  if (!open || !user) return null;

  const role = user.role || 'user';
  const items = WHATS_NEW.filter((i) => i.roles.includes(role));

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, WHATS_NEW_VERSION);
    setWants(false);
    window.dispatchEvent(new CustomEvent('whatsnew-dismissed'));
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      data-testid="whats-new-dialog"
    >
      <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-[#1A2433] border border-slate-200 dark:border-[#2D3748] max-h-[85vh] flex flex-col">
        <div className="relative px-6 py-5" style={{ background: 'linear-gradient(120deg, #1E88E5, #7C3AED)' }}>
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="whats-new-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-4 h-4" />
            {firstTime ? 'Welcome to HinesTime' : `What's new · v${WHATS_NEW_VERSION}`}
          </div>
          <h2 className="text-xl font-bold text-white mt-1">
            {firstTime
              ? `Hi ${(user.name || '').split(' ')[0] || 'there'} — here's the quick tour`
              : 'New since your last visit'}
          </h2>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {firstTime && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">The essentials</div>
              <ul className="space-y-2.5">
                {CORE_FEATURES.map((f, i) => (
                  <li key={`core-${i}`} className="flex gap-3 text-sm text-slate-700 dark:text-slate-200">
                    <f.icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              {firstTime ? 'Fresh in this release' : 'Highlights'}
            </div>
            <ul className="space-y-3">
              {items.map((f, i) => (
                <li key={`new-${i}`} className="flex gap-3" data-testid={`whats-new-item-${i}`}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-violet-500/10">
                    <f.icon className="w-4 h-4 text-violet-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{f.title}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{f.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-[#2D3748] flex items-center justify-between gap-3">
          <button
            onClick={dismiss}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            data-testid="whats-new-skip"
          >
            Skip
          </button>
          <div className="flex items-center gap-3">
            <a
              href="/whats-new"
              onClick={dismiss}
              className="text-sm font-medium text-slate-500 hover:text-slate-400"
              data-testid="whats-new-changelog-link"
            >
              Full changelog
            </a>
            <a
              href="/user-manual"
              onClick={dismiss}
              className="text-sm font-medium text-blue-500 hover:text-blue-400"
              data-testid="whats-new-manual-link"
            >
              Read the manual
            </a>
            <button
              onClick={dismiss}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              style={{ background: '#1E88E5' }}
              data-testid="whats-new-got-it"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewDialog;
